/**
 * 5897 — Simulador de evento antes da abertura das vendas: produtor, cidade,
 * local, data, estilo, preço, artistas e a estrutura planejada → faixa de
 * público e de valor pelo modelo ex-ante (GBM). É uma faixa larga de propósito:
 * antes de vender, o erro típico vai de 32% (produtor que já fez evento no
 * mesmo local) a 54% (produtor novo), validado em eventos de jul/2025 a
 * out/2026. Os eventos parecidos aparecem só como referência.
 */
import { FC, FormEvent, useMemo, useState } from 'react'
import styled from 'styled-components'
import { simularPrevisao, usePrevisoes, useSimuladorOpcoes } from '../../../hooks/previsoes'
import { EntradaSimulador, GrupoSimulador, ResultadoSimulador } from '../../../types/previsoes'
import { BD, Button, Card, Field, FiltersRow, Input, PageSubtitle, PageTitle, Select, Spinner, Table } from '../styles'
import { CartaoTabela, Entenda, Kpi, Kpis, Nota, Rolagem, Selo, brl, fmtInt, pct } from './comum'

const MAX_ARTISTAS = 10
/** mesmo teto da API (simulador.ts PRECO_MAXIMO) */
const PRECO_MAXIMO = 20000

const GRUPO_TEXTO: Record<GrupoSimulador, string> = {
  produtor_local: 'o produtor já fez eventos neste local',
  produtor: 'o produtor tem histórico na BD, mas não neste local',
  novo: 'produtor sem histórico na BD (ou não informado)',
}

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  button {
    font: inherit;
    font-size: 12.5px;
    border: 1px solid ${BD.grayBorder};
    background: ${BD.grayBg};
    border-radius: 9999px;
    padding: 3px 10px;
    cursor: pointer;
  }
`

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const hoje = (): string => iso(new Date())
const amanha = (): string => iso(new Date(Date.now() + 86400000))

/** Razão valor total ÷ valor de face da plataforma nos últimos 120 dias (do snapshot). */
function useRazaoTotalFace(): number | null {
  const { painel } = usePrevisoes()
  return useMemo(() => {
    if (!painel) return null
    const soma = (m: Record<string, number>): number => Object.keys(m).reduce((s, k) => s + m[k], 0)
    const face = soma(painel.face_diario)
    return face > 0 ? soma(painel.gmv_diario) / face : null
  }, [painel])
}

const num = (v: number | null): string => (v !== null ? fmtInt(v) : '—')

const Resultado: FC<{ r: ResultadoSimulador; entrada: EntradaSimulador; razao: number | null }> = ({ r, entrada, razao }) => {
  if (!r.suficiente || !r.publico || !r.face || !r.grupo) {
    return (
      <Nota $tom="atencao">
        O modelo não conseguiu estimar esta combinação. Revise a cidade, o estilo e o preço e tente de novo.
      </Nota>
    )
  }
  const q = r.qualidade[r.grupo]
  const p = r.produtor
  return (
    <>
      <Nota $tom={r.confianca === 'baixa' ? 'atencao' : 'info'}>
        Confiança {r.confianca}: {GRUPO_TEXTO[r.grupo]}. Em eventos assim, a estimativa central errou em mediana{' '}
        {pct(q.erro_mediano)} e o resultado caiu dentro da faixa em {pct(q.cobertura)} dos casos ({fmtInt(q.n)} eventos
        de jul/2025 a out/2026).
      </Nota>
      <Kpis>
        <Kpi
          rotulo="Público pagante estimado"
          valor={fmtInt(r.publico.p50)}
          dica={`faixa 80%: ${fmtInt(r.publico.p10)} – ${fmtInt(r.publico.p90)}`}
        />
        <Kpi
          rotulo="Valor de face estimado"
          valor={brl(r.face.p50)}
          dica={`faixa: ${brl(r.face.p10)} – ${brl(r.face.p90)} · a ${brl(entrada.preco)} por ingresso`}
        />
        {razao !== null && (
          <Kpi
            rotulo="Valor total estimado"
            valor={brl(r.face.p50 * razao)}
            dica={`com taxas e juros na proporção da plataforma (×${razao.toLocaleString('pt-BR', { maximumFractionDigits: 2 })})`}
          />
        )}
        {p && (
          <Kpi
            rotulo="Histórico do produtor"
            valor={`${fmtInt(p.eventos)} eventos`}
            dica={`média ${num(p.media)} pagos · último ${num(p.ultimo)} · maior ${num(p.maximo)}`}
          />
        )}
      </Kpis>

      {p && (p.eventos_local > 0 || p.eventos_mesmo_nome > 0) && (
        <Nota>
          {p.nome || `Produtor ${p.id}`}: {p.eventos_local > 0 ? `${fmtInt(p.eventos_local)} eventos neste local (média de ${num(p.media_local)} pagos)` : 'sem eventos neste local'}
          {p.eventos_mesmo_nome > 0
            ? `; ${fmtInt(p.eventos_mesmo_nome)} edições anteriores com este nome (média de ${num(p.media_mesmo_nome)} pagos).`
            : '.'}{' '}
          É o que mais pesa na estimativa.
        </Nota>
      )}

      {r.artistas.length > 0 && (
        <CartaoTabela style={{ marginBottom: 16 }}>
          <div className="titulo">
            <h3>Histórico dos artistas na BD</h3>
            <div style={{ fontSize: 12.5, color: BD.grayText }}>
              Pagos por evento como atração (2019 e 2022+). O mesmo artista varia em mediana 4,1× entre estados.
            </div>
          </div>
          <Rolagem>
            <Table>
              <thead>
                <tr>
                  <th>Artista</th>
                  <th className="n">Eventos</th>
                  <th className="n">UFs</th>
                  <th className="n">Mediana geral</th>
                  <th className="n">Eventos em {entrada.uf}</th>
                  <th className="n">Mediana em {entrada.uf}</th>
                </tr>
              </thead>
              <tbody>
                {r.artistas.map((a) => (
                  <tr key={a.nome} style={{ cursor: 'default' }}>
                    <td style={{ fontWeight: 500 }}>{a.nome}</td>
                    <td className="n">{a.eventos}</td>
                    <td className="n">{a.ufs}</td>
                    <td className="n">{a.mediana !== null ? fmtInt(a.mediana) : '—'}</td>
                    <td className="n">{a.eventos_uf}</td>
                    <td className="n">{a.mediana_uf !== null ? fmtInt(a.mediana_uf) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Rolagem>
        </CartaoTabela>
      )}

      <CartaoTabela>
        <div className="titulo">
          <h3>Eventos parecidos, para referência</h3>
          <div style={{ fontSize: 12.5, color: BD.grayText }}>
            Não entram na conta: servem para comparar. Escore: mesmo artista 4 (+2 na mesma UF), mesmo local 2, mesma
            cidade 1,5, mesmo estilo 1,5, mesma UF 1, preço parecido 1, mesmo tipo de dia 0,5.
          </div>
        </div>
        {(r.comparaveis ?? []).length === 0 ? (
          <div style={{ padding: '12px 16px', color: BD.grayText, fontSize: 13 }}>Nenhum evento realizado parecido o bastante.</div>
        ) : (
          <Rolagem>
            <Table>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th className="n">Escore</th>
                  <th>Por quê</th>
                  <th className="n">Preço médio</th>
                  <th className="n">Pagos</th>
                </tr>
              </thead>
              <tbody>
                {(r.comparaveis ?? []).map((c) => (
                  <tr key={c.id} style={{ cursor: 'default' }}>
                    <td style={{ fontWeight: 500, maxWidth: 340 }}>
                      {c.nome}
                      <small>
                        {c.cidade}
                        {c.local ? ` · ${c.local}` : ''} · {c.data}
                      </small>
                    </td>
                    <td className="n">{String(c.escore).replace('.', ',')}</td>
                    <td>
                      {c.motivos.map((m) => (
                        <Selo key={m} tom="neutro">
                          {m}
                        </Selo>
                      ))}
                    </td>
                    <td className="n">{brl(c.preco_medio)}</td>
                    <td className="n">{fmtInt(c.pagos)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Rolagem>
        )}
      </CartaoTabela>
    </>
  )
}

export const PrevisoesSimulador: FC = () => {
  const { opcoes, isLoading, isError } = useSimuladorOpcoes()
  const razao = useRazaoTotalFace()
  const [produtorBusca, setProdutorBusca] = useState('')
  const [produtorId, setProdutorId] = useState<number | null>(null)
  const [uf, setUf] = useState('')
  const [cidade, setCidade] = useState('')
  const [localId, setLocalId] = useState('')
  const [nome, setNome] = useState('')
  const [estilo, setEstilo] = useState('')
  const [preco, setPreco] = useState('')
  const [data, setData] = useState(amanha())
  const [hora, setHora] = useState('20')
  const [abertura, setAbertura] = useState(hoje())
  const [tiposIngresso, setTiposIngresso] = useState('4')
  const [setores, setSetores] = useState('2')
  const [lotes, setLotes] = useState('3')
  const [artistas, setArtistas] = useState<string[]>([])
  const [artistaBusca, setArtistaBusca] = useState('')
  const [rodando, setRodando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [resultado, setResultado] = useState<{ r: ResultadoSimulador; entrada: EntradaSimulador } | null>(null)

  // qualquer mudança na entrada invalida o resultado anterior (ele seria de outra combinação)
  const mudou = (): void => {
    setResultado(null)
    setErro(null)
  }

  const cidades = useMemo(() => (opcoes?.cidades ?? []).filter((c) => c.uf === uf), [opcoes, uf])
  const locais = useMemo(() => (opcoes?.locais ?? []).filter((l) => l.uf === uf && l.cidade === cidade), [opcoes, uf, cidade])
  const nomesArtistas = useMemo(() => new Set((opcoes?.artistas ?? []).map((a) => a.nome)), [opcoes])
  const produtoresPorRotulo = useMemo(() => {
    const m = new Map<string, number>()
    ;(opcoes?.produtores ?? []).forEach((p) => m.set(`${p.nome} (#${p.id})`, p.id))
    return m
  }, [opcoes])

  if (isLoading) return <Spinner />
  if (isError || !opcoes) return <PageSubtitle>Não foi possível carregar o simulador. Tente de novo em instantes.</PageSubtitle>
  const L = opcoes.limites ?? { tiposIngresso: 500, setores: 2000, lotes: 50, janelaDias: 400, nome: 200 }

  const adicionarArtista = (n0: string): void => {
    const n = n0.trim()
    if (!nomesArtistas.has(n) || artistas.indexOf(n) >= 0 || artistas.length >= MAX_ARTISTAS) return
    setArtistas([...artistas, n])
    setArtistaBusca('')
    mudou()
  }
  const inteiroOk = (v: string, max: number): boolean => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= max
  const precoInvalido = preco !== '' && !(Number(preco) > 0 && Number(preco) <= PRECO_MAXIMO)
  const janela = data && abertura ? (Date.parse(`${data}T00:00:00Z`) - Date.parse(`${abertura}T00:00:00Z`)) / 86400000 : NaN
  const aberturaInvalida = !(janela >= 0 && janela <= L.janelaDias)
  const estruturaOk = inteiroOk(tiposIngresso, L.tiposIngresso) && inteiroOk(setores, L.setores) && inteiroOk(lotes, L.lotes)
  const horaOk = /^\d{1,2}$/.test(hora) && Number(hora) <= 23
  // texto no campo de produtor que não é uma opção da lista não pode virar "produtor novo" em silêncio
  const produtorOk = produtorBusca === '' || produtorId !== null
  const dataPassada = data !== '' && data < hoje()
  const pronto =
    uf && cidade && estilo && preco !== '' && !precoInvalido && data && !dataPassada && !aberturaInvalida && estruturaOk && horaOk && produtorOk
  const enviar = async (ev: FormEvent): Promise<void> => {
    ev.preventDefault()
    if (!pronto) return
    const entrada: EntradaSimulador = {
      uf,
      cidade,
      localId: localId ? Number(localId) : null,
      estilo,
      preco: Number(preco),
      data,
      artistas,
      produtorId,
      nome: nome.trim().slice(0, L.nome),
      abertura,
      hora: Number(hora),
      tiposIngresso: Number(tiposIngresso),
      setores: Number(setores),
      lotes: Number(lotes),
    }
    setRodando(true)
    setErro(null)
    try {
      setResultado({ r: await simularPrevisao(entrada), entrada })
    } catch {
      setErro('Não foi possível simular agora. Tente de novo em instantes.')
    } finally {
      setRodando(false)
    }
  }

  return (
    <>
      <PageTitle>Simulador de evento</PageTitle>
      <PageSubtitle>
        Antes de abrir as vendas: informe produtor, onde, quando, o estilo, o preço, a estrutura planejada e os artistas, e
        veja a faixa de público e de valor estimada pelo modelo. Base de eventos até{' '}
        {opcoes.dados_ate.split('-').reverse().join('/')}.
      </PageSubtitle>

      <FiltersRow as="form" onSubmit={enviar} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <Field style={{ gridColumn: '1 / span 2' }}>
          <label htmlFor="sim-produtor">Produtor (opcional)</label>
          <Input
            id="sim-produtor"
            type="search"
            list="sim-produtores"
            placeholder="Digite e escolha da lista — produtores com eventos na BD"
            value={produtorBusca}
            onChange={(e) => {
              setProdutorBusca(e.target.value)
              setProdutorId(produtoresPorRotulo.get(e.target.value) ?? null)
              mudou()
            }}
          />
          <datalist id="sim-produtores">
            {opcoes.produtores.map((p) => (
              <option key={p.id} value={`${p.nome} (#${p.id})`}>
                {p.eventos} eventos · {p.uf}
              </option>
            ))}
          </datalist>
          {produtorBusca !== '' && produtorId === null && (
            <span style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>Escolha um produtor da lista ou deixe em branco (produtor novo).</span>
          )}
        </Field>
        <Field style={{ gridColumn: 'span 2' }}>
          <label htmlFor="sim-nome">Nome do evento (opcional)</label>
          <Input
            id="sim-nome"
            type="text"
            maxLength={L.nome}
            placeholder="liga às edições anteriores do mesmo produtor"
            value={nome}
            onChange={(e) => {
              setNome(e.target.value)
              mudou()
            }}
          />
        </Field>
        <Field>
          <label htmlFor="sim-uf">UF</label>
          <Select
            id="sim-uf"
            value={uf}
            onChange={(e) => {
              setUf(e.target.value)
              setCidade('')
              setLocalId('')
              mudou()
            }}
          >
            <option value="">Escolha</option>
            {opcoes.ufs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <label htmlFor="sim-cidade">Cidade</label>
          <Select
            id="sim-cidade"
            value={cidade}
            disabled={!uf}
            onChange={(e) => {
              setCidade(e.target.value)
              setLocalId('')
              mudou()
            }}
          >
            <option value="">Escolha</option>
            {cidades.map((c) => (
              <option key={c.cidade} value={c.cidade}>
                {c.cidade} ({c.eventos})
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <label htmlFor="sim-local">Local (opcional)</label>
          <Select id="sim-local" value={localId} disabled={!cidade} onChange={(e) => {
              setLocalId(e.target.value)
              mudou()
            }}>
            <option value="">Qualquer local</option>
            {locais.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nome} ({l.eventos})
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <label htmlFor="sim-estilo">Estilo</label>
          <Select id="sim-estilo" value={estilo} onChange={(e) => {
              setEstilo(e.target.value)
              mudou()
            }}>
            <option value="">Escolha</option>
            {opcoes.estilos.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <label htmlFor="sim-data">Data do evento</label>
          <Input id="sim-data" type="date" min={hoje()} value={data} aria-invalid={dataPassada} onChange={(e) => {
              setData(e.target.value)
              mudou()
            }} />
          {dataPassada && <span style={{ fontSize: 12, color: '#B42318', marginTop: 4 }}>O simulador é para eventos que ainda vão acontecer.</span>}
        </Field>
        <Field>
          <label htmlFor="sim-hora">Hora de início</label>
          <Input id="sim-hora" type="number" min={0} max={23} step={1} value={hora} aria-invalid={!horaOk} onChange={(e) => {
              setHora(e.target.value)
              mudou()
            }} />
        </Field>
        <Field>
          <label htmlFor="sim-abertura">Abertura das vendas</label>
          <Input id="sim-abertura" type="date" value={abertura} aria-invalid={aberturaInvalida} onChange={(e) => {
              setAbertura(e.target.value)
              mudou()
            }} />
          {aberturaInvalida && (
            <span style={{ fontSize: 12, color: '#B42318', marginTop: 4 }}>
              A abertura deve ser até a data do evento e no máximo {L.janelaDias} dias antes.
            </span>
          )}
        </Field>
        <Field>
          <label htmlFor="sim-preco">Preço médio do ingresso (R$)</label>
          <Input
            id="sim-preco"
            type="number"
            min={1}
            max={PRECO_MAXIMO}
            step="0.01"
            inputMode="decimal"
            placeholder="ex.: 120"
            value={preco}
            onChange={(e) => {
              setPreco(e.target.value)
              mudou()
            }}
            aria-invalid={precoInvalido}
          />
          {precoInvalido && (
            <span style={{ fontSize: 12, color: '#B42318', marginTop: 4 }}>
              Informe um preço médio entre R$ 1 e {brl(PRECO_MAXIMO)}.
            </span>
          )}
        </Field>
        <Field>
          <label htmlFor="sim-ingressos">Tipos de ingresso</label>
          <Input id="sim-ingressos" type="number" min={1} max={L.tiposIngresso} step={1} value={tiposIngresso} aria-invalid={!inteiroOk(tiposIngresso, L.tiposIngresso)} onChange={(e) => {
              setTiposIngresso(e.target.value)
              mudou()
            }} />
        </Field>
        <Field>
          <label htmlFor="sim-setores">Setores</label>
          <Input id="sim-setores" type="number" min={1} max={L.setores} step={1} value={setores} aria-invalid={!inteiroOk(setores, L.setores)} onChange={(e) => {
              setSetores(e.target.value)
              mudou()
            }} />
        </Field>
        <Field>
          <label htmlFor="sim-lotes">Lotes</label>
          <Input id="sim-lotes" type="number" min={1} max={L.lotes} step={1} value={lotes} aria-invalid={!inteiroOk(lotes, L.lotes)} onChange={(e) => {
              setLotes(e.target.value)
              mudou()
            }} />
        </Field>
        <Field style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="sim-artista">Artistas (opcional, até {MAX_ARTISTAS})</label>
          <Input
            id="sim-artista"
            type="search"
            list="sim-artistas"
            placeholder="Digite e escolha da lista — só artistas com histórico na BD"
            value={artistaBusca}
            onChange={(e) => {
              setArtistaBusca(e.target.value)
              if (nomesArtistas.has(e.target.value)) adicionarArtista(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                adicionarArtista(artistaBusca)
              }
            }}
          />
          <datalist id="sim-artistas">
            {opcoes.artistas.map((a) => (
              <option key={a.nome} value={a.nome}>
                {a.eventos} eventos
              </option>
            ))}
          </datalist>
          {artistas.length > 0 && (
            <Chips>
              {artistas.map((a) => (
                <button key={a} type="button" aria-label={`Remover ${a}`} onClick={() => {
                  setArtistas(artistas.filter((x) => x !== a))
                  mudou()
                }}>
                  {a} ×
                </button>
              ))}
            </Chips>
          )}
        </Field>
        <Field style={{ justifyContent: 'flex-end' }}>
          <Button type="submit" disabled={!pronto || rodando}>
            {rodando ? 'Simulando…' : 'Simular'}
          </Button>
        </Field>
      </FiltersRow>

      {erro && <Nota $tom="atencao">{erro}</Nota>}
      {resultado && <Resultado r={resultado.r} entrada={resultado.entrada} razao={razao} />}
      {!resultado && (
        <Card style={{ color: BD.grayText, fontSize: 14 }}>
          Preencha UF, cidade, data, estilo, preço e a estrutura planejada. O produtor é o que mais aproxima a estimativa:
          quando ele já fez eventos no mesmo local o erro típico é {pct(opcoes.qualidade.produtor_local.erro_mediano)};
          com histórico em outro local, {pct(opcoes.qualidade.produtor.erro_mediano)}; produtor novo,{' '}
          {pct(opcoes.qualidade.novo.erro_mediano)}.
        </Card>
      )}
      <Entenda
        paragrafos={[
          'Como funciona: um modelo de árvores (gradient boosting) treinado nos eventos realizados (2019 e 2022 em diante, sem a pandemia) estima o público pagante a partir do que se sabe antes de abrir: produtor e seu histórico (média, último, maior evento, no mesmo local e com o mesmo nome), local, cidade e estilo, data e hora, preço médio, tipos de ingresso, setores, lotes, dias entre a abertura e o evento, e artistas. As variáveis que mais pesam são a estrutura do evento em relação ao usual do produtor (mais tipos de ingresso e preço mais alto que o normal dele indicam evento maior).',
          'A faixa de 80% foi medida nos erros reais do próprio modelo, simulando cada evento de jul/2025 a out/2026 só com o que existia antes de ele abrir as vendas. Por isso é larga: entre edições do mesmo evento no mesmo local o público varia de ÷3 a ×2,6.',
          'Valor de face = público × preço informado. Valor total aplica a proporção entre o total pago e o valor de face da plataforma nos últimos 120 dias.',
          'Limites: o banco não tem cadastro de artistas (vêm do texto dos eventos e cobrem ~25% deles), nem capacidade do local, nem campanhas. Assim que o evento abre e chega a 20 pagos, use a projeção da Carteira, que erra 17,6% a uma semana do evento.',
        ]}
      />
    </>
  )
}
